import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, LineChart, Handshake, Shield, TrendingUp } from 'lucide-react';
import { AnimatedArrow } from '../components/AnimatedArrow';
import { DecorativeSquare } from '../components/DecorativeSquare';
import { ConstructionBackground } from '../components/ConstructionBackground';
import { StatusToggle } from '../components/StatusToggle';
import { PartnerCarousel } from '../components/PartnerCarousel';
import { BenefitsCarousel } from '../components/BenefitsCarousel';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { FadeInOnScroll, ScaleOnScroll, StaggerChildren } from '../components/ParallaxEffects';
import { FloatingShapes, ScrollProgress } from '../components/FloatingShapes';
import { useSEO } from '../hooks/useSEO';
import { BlogPreview } from '../components/BlogPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lazy-loaded Video Component for better performance
const LazyVideo = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const optimizedVideo = "https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/sdl0tx45_LIVAG_Landingpage_Back_downsized.webm";
  const fallbackMp4 = "https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/8g8hm5hk_LIVAG_Landingpage_Back%20%283%29.mp4";

  return (
    <div ref={containerRef} className="absolute inset-0 w-full h-full">
      {/* Solid color fallback - shows immediately */}
      {!videoLoaded && (
        <div className="absolute inset-0 w-full h-full bg-[#264F77]" />
      )}
      {/* Video - loads only when visible */}
      {isVisible && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
          title="LIVAG Hintergrundvideo"
        >
          {/* Optimized WebM for all devices */}
          <source src={optimizedVideo} type="video/webm" />
          {/* MP4 fallback for Safari */}
          <source src={fallbackMp4} type="video/mp4" />
        </video>
      )}
    </div>
  );
};

export const Home = () => {
  useSEO('home');
  const [featuredProjects, setFeaturedProjects] = useState([]);

  useEffect(() => {
    fetchFeaturedProjects();
  }, []);

  const fetchFeaturedProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/featured`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedProjects(data);
      }
    } catch (err) {
      console.error('Error fetching featured projects:', err);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Floating geometric shapes in background */}
      <FloatingShapes />
      
      {/* Scroll progress indicator */}
      <ScrollProgress />
      
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background Container */}
        <div className="absolute inset-0 w-full h-full overflow-hidden bg-[#264F77]">
          {/* Lazy-loaded responsive Video */}
          <LazyVideo />
        </div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/50 z-20"></div>

        {/* Hero Content */}
        <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-normal mb-4 sm:mb-6 leading-tight px-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Nachhaltige<br /> Immobilienentwicklung
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 max-w-3xl mx-auto font-light px-4">
            In der Grossregion St. Gallen, Gossau, Wil, Rorschach und Herisau
          </p>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-8 sm:mb-12 max-w-4xl mx-auto text-gray-200 px-4">
            Wir entwickeln und vermitteln Immobilienprojekte mit architektonischer Qualität – für nachhaltige Lebensräume in der Ostschweiz.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center flex-wrap">
            <Link
              to="/projekte"
              className="group inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 bg-white text-[#264F77] font-semibold rounded-md hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Unsere Projekte
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
            <Link
              to="/kontakt"
              className="group inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 bg-white/10 backdrop-blur-md text-white font-semibold rounded-md hover:bg-white/20 transition-all duration-300 border-2 border-white/40 hover:border-white/60 transform hover:-translate-y-1"
            >
              Jetzt Immobilie bewerten lassen
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
            <Link
              to="/immobilien-verkauf"
              className="group inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 bg-[#264F77] text-white font-semibold rounded-md hover:bg-[#1e3d5c] transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 border-2 border-white/30"
            >
              Kostenloses Erstgespräch vereinbaren
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Partner Carousel Section with solid background */}
      <section className="bg-[#264F77] overflow-visible py-2">
        <PartnerCarousel />
      </section>

      {/* Immobilien-Verkauf Banner */}
      <section className="relative bg-[#264F77]/[0.92] py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-normal text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Mehrfamilienhäuser und Renditeobjekte zu Bestpreisen
          </h2>
          <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed">
            Direktankauf oder strukturierte Off-Market-Transaktionen mit qualifizierten Investoren
          </p>
          <Link
            to="/immobilien-verkauf"
            className="inline-flex items-center px-8 py-4 bg-white text-[#264F77] font-semibold text-lg rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Immobilie verkaufen oder anbieten
            <ArrowRight className="ml-3" size={20} />
          </Link>
        </div>
      </section>

      {/* About Section with Animated Construction Background */}
      <ConstructionBackground>
        <div className="pt-32 pb-0 relative overflow-visible">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInOnScroll direction="up" duration={1}>
              <div className="flex flex-col lg:flex-row items-end gap-10">
                <div className="flex-1 bg-white/55 backdrop-blur-sm p-10 rounded-2xl shadow-xl mb-20 lg:mb-32 lg:mr-80">
                  <h2 className="text-5xl md:text-6xl font-normal text-[#264F77] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                    LIVAG.
                  </h2>
                  <p className="text-xl text-gray-700 leading-relaxed mb-6">
                    Die LIVAG entwickelt hochwertige Immobilien mit langfristigem Mehrwert. Als inhabergeführte Gesellschaft aus St. Gallen schaffen wir Lebensräume, die Architektur, Nachhaltigkeit und Wirtschaftlichkeit vereinen.
                  </p>
                  <p className="text-xl text-gray-700 leading-relaxed mb-10">
                    Unsere Projekte setzen neue Standards für modernes Wohnen und Arbeiten in der Ostschweiz.
                  </p>
                  <Link
                    to="/ueber-uns"
                    className="group inline-flex items-center text-[#264F77] font-semibold text-lg hover:text-[#1e3d5c] transition-colors"
                  >
                    Mehr über uns erfahren
                    <AnimatedArrow className="ml-2" />
                  </Link>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
          {/* Christoph Jermann Photo - positioned at section break, far right */}
          <div className="hidden lg:block absolute right-4 xl:right-16 bottom-0 z-20">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/yprdkmz4_Christoph%20Jermann_Hintergrund%20transparent.webp"
                alt="Christoph Jermann - Immobilienexperte und Mitglied der Geschäftsleitung bei LIVAG AG St. Gallen"
                title="Christoph Jermann - Ihr Ansprechpartner für Haus kaufen und verkaufen in der Ostschweiz"
                loading="lazy"
                width="384"
                height="512"
                className="w-80 xl:w-96 h-auto drop-shadow-2xl"
              />
              <div className="absolute right-4 -top-28 text-left bg-white/90 backdrop-blur-sm px-4 py-3 rounded-lg shadow-lg">
                <p className="text-[#264F77] font-semibold text-lg">Christoph Jermann</p>
                <p className="text-gray-600 text-sm">Mitglied der Geschäftsleitung</p>
                <p className="text-gray-500 text-xs">Immobilienökonom MAS FHO</p>
                <p className="text-gray-500 text-xs">eidg. Dipl. Betriebswirtschafter</p>
              </div>
            </div>
          </div>
        </div>
      </ConstructionBackground>

      {/* Services Section */}
      <section className="pt-8 pb-16 relative overflow-hidden">
        {/* Fixed Parallax Background with Blue Elements */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_eb9172b8-f224-4b6b-a2dc-b045c6adcb9e/artifacts/jziahget_Hintergrund%20mit%20Gold_1.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        />
        
        {/* Blue Geometric Elements - Fixed Position */}
        <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none" style={{ backgroundAttachment: 'fixed' }}>
          {/* Large circle top left */}
          <div 
            className="absolute -top-20 -left-20 w-96 h-96 border-2 border-white/20 rounded-full"
            style={{ position: 'fixed', top: '-5rem', left: '-5rem' }}
          />
          {/* Medium circle top right */}
          <div 
            className="absolute top-40 -right-10 w-64 h-64 border-2 border-white/15 rounded-full"
            style={{ position: 'fixed', top: '10rem', right: '-2.5rem' }}
          />
          {/* Square rotated */}
          <div 
            className="absolute top-1/2 left-1/4 w-32 h-32 border-2 border-white/10 rotate-45"
            style={{ position: 'fixed', top: '50%', left: '25%' }}
          />
          {/* Small circles */}
          <div 
            className="absolute bottom-20 left-10 w-20 h-20 bg-white/5 rounded-full"
            style={{ position: 'fixed', bottom: '5rem', left: '2.5rem' }}
          />
          <div 
            className="absolute top-20 right-1/3 w-16 h-16 bg-white/5 rounded-full"
            style={{ position: 'fixed', top: '5rem', right: '33%' }}
          />
          {/* Diagonal lines */}
          <svg className="absolute inset-0 w-full h-full" style={{ position: 'fixed' }}>
            <line x1="0" y1="100%" x2="30%" y2="60%" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            <line x1="70%" y1="0" x2="100%" y2="40%" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            <line x1="50%" y1="100%" x2="80%" y2="50%" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          </svg>
        </div>
        
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-[#264F77]/75 z-[2] pointer-events-none"></div>
        
        <DecorativeSquare size="md" className="absolute top-10 right-20 opacity-20 z-10" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <FadeInOnScroll direction="up" duration={0.8}>
            <h2 className="text-5xl md:text-6xl font-normal text-white text-center mb-12" style={{ fontFamily: "'Playfair Display', serif" }}>
              Unsere Leistungen - Ihr Nutzen.
            </h2>
          </FadeInOnScroll>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-10" staggerDelay={0.15}>
            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-lg shadow-lg flex flex-col h-full card-parallax text-center items-center">
              <div className="w-14 h-14 bg-[#264F77]/10 rounded-lg flex items-center justify-center mb-6">
                <TrendingUp className="text-[#264F77]" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-[#264F77] mb-4">
                Optimierungen & Entwicklungen
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm mb-4 flex-grow">
                Wir erkennen individuelle Potenziale, entwickeln Bestehendes weiter und optimieren gezielt die Renditen Ihres Immobilienportfolios als Immobilienentwickler und -vermittler in der Region.
              </p>
              <p className="text-[#264F77] font-medium text-sm italic mb-6">
                «partnerschaftlich, werthaltig und renditeoptimiert»
              </p>
              <Link to="/ueber-uns" className="group inline-flex items-center text-[#264F77] font-semibold text-sm hover:text-[#1e3d5c] transition-colors min-h-[48px] min-w-[48px] py-2" aria-label="Mehr über LIVAG Immobilienentwicklung erfahren">
                Mehr über uns erfahren
                <AnimatedArrow className="ml-2" />
              </Link>
            </div>
            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-lg shadow-lg flex flex-col h-full card-parallax text-center items-center">
              <div className="w-14 h-14 bg-[#264F77]/10 rounded-lg flex items-center justify-center mb-6">
                <Handshake className="text-[#264F77]" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-[#264F77] mb-4">
                Liegenschaftskauf & Immobilienvermarktung
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm mb-4 flex-grow">
                Als lösungsorientierter Immobilienmakler in St. Gallen, Appenzell und Zürich unterstützen wir Sie aktiv in der Vermarktung, bieten objektive Wertanalysen oder prüfen den Zukauf Ihrer Liegenschaft.
              </p>
              <p className="text-[#264F77] font-medium text-sm italic mb-6">
                «engagiert, kostensparend und effizient»
              </p>
              <Link to="/ueber-uns" className="group inline-flex items-center text-[#264F77] font-semibold text-sm hover:text-[#1e3d5c] transition-colors min-h-[48px] min-w-[48px] py-2" aria-label="Mehr über LIVAG Immobilienvermarktung erfahren">
                Mehr über Vermarktung erfahren
                <AnimatedArrow className="ml-2" />
              </Link>
            </div>
            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-lg shadow-lg flex flex-col h-full card-parallax text-center items-center">
              <div className="w-14 h-14 bg-[#264F77]/10 rounded-lg flex items-center justify-center mb-6">
                <Compass className="text-[#264F77]" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-[#264F77] mb-4">
                Planung & Bauleitung
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm mb-4 flex-grow">
                Wir holen das Beste aus Ihrem Grundstück oder Ihrer Immobilie heraus! Von der Planung über Bauleitung bis zur Übergabe stehen wir Ihnen als Immobilienentwickler und -vermittler zur Seite.
              </p>
              <p className="text-[#264F77] font-medium text-sm italic mb-6">
                «nachhaltig, ressourcenschonend und energieeffizient»
              </p>
              <Link to="/ueber-uns" className="group inline-flex items-center text-[#264F77] font-semibold text-sm hover:text-[#1e3d5c] transition-colors min-h-[48px] min-w-[48px] py-2" aria-label="Mehr über LIVAG Planung und Bauleitung erfahren">
                Mehr über Planung erfahren
                <AnimatedArrow className="ml-2" />
              </Link>
            </div>
          </StaggerChildren>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInOnScroll direction="up" duration={0.8}>
            <div className="text-center mb-24">
              <h2 className="text-5xl md:text-6xl font-normal text-[#264F77] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Ausgewählte Projekte.
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Einblick in unsere aktuellen und realisierten Entwicklungen
            </p>
            </div>
          </FadeInOnScroll>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-10" staggerDelay={0.2}>
            {featuredProjects.map((project, index) => (
              <Link
                key={project.id}
                to={`/projekte/${project.id}`}
                className="group block bg-white rounded-lg overflow-hidden shadow-md card-parallax"
              >
                <div className="relative h-80 overflow-hidden image-zoom-container">
                  <img
                    src={project.image}
                    alt={`${project.title} - ${project.type} in ${project.location} | Haus kaufen Ostschweiz`}
                    title={`${project.title} - ${project.status} | LIVAG Immobilienprojekt`}
                    loading="lazy"
                    width="400"
                    height="320"
                    className="w-full h-full object-cover image-zoom"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute top-6 right-6">
                    <span className="px-4 py-2 bg-[#264F77] text-white text-xs font-semibold rounded-full shadow-lg">
                      {project.status}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-white transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-200 text-sm">{project.location}</p>
                  </div>
                </div>
                <div className="p-8 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                  <div className="flex items-center text-[#264F77] font-semibold min-h-[48px]">
                    Projekt {project.title} ansehen
                    <AnimatedArrow className="ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </StaggerChildren>
          <FadeInOnScroll direction="up" delay={0.3}>
            <div className="text-center mt-16">
              <Link
                to="/projekte"
                className="inline-flex items-center justify-center px-10 py-4 bg-[#264F77] text-white font-semibold rounded-md hover:bg-[#1e3d5c] transition-all duration-300 shadow-lg hover:shadow-xl btn-primary-hover min-h-[48px]"
                aria-label="Alle LIVAG Immobilienprojekte ansehen"
              >
                Alle Projekte ansehen
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Benefits Carousel - After Projects Section */}
      <BenefitsCarousel />

      {/* CTA Section */}
      <section className="py-24 bg-[#264F77] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Bereit für Ihr nächstes Projekt?
          </h2>
          <p className="text-xl mb-8 text-gray-200 max-w-2xl mx-auto">
            Kontaktieren Sie uns für ein unverbindliches Gespräch über Ihre Immobilienentwicklung.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#264F77] font-semibold rounded-md hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl min-h-[48px]"
            aria-label="Kontaktformular öffnen für Immobilienberatung"
          >
            Jetzt Immobilie bewerten lassen
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Testimonial Carousel - Before Blog */}
      <TestimonialCarousel />

      {/* Blog Preview - Latest 3 Posts */}
      <BlogPreview />
    </div>
  );
};
